import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {DoctorService} from '../../services/doctor/doctor.service';
import {ReservedAppointmentModel} from '../../models/appointment/appointment.model';
import {iResponse, iResponseList} from '../../models/response/response.model';
import {MatDialog} from '@angular/material/dialog';
import {ApplicationCancelDialogComponent} from '../application-cancel-dialog/application-cancel-dialog.component';
import {Router} from "@angular/router";
import {QueryModel} from '../../services/http/http.service';
import {PatientService} from '../../services/patient/patient.service';
import {PatientModel} from '../../models/patient/patient.model';
import {DoctorModel} from '../../models/doctor/doctor.model';
import {InfoMessageService} from '../../services/infomessage/infomessage.service';
import {FullCalendarComponent, FullCalendarModule} from '@fullcalendar/angular';
import {CalendarOptions} from "@fullcalendar/angular"
import dayGridPlugin from "@fullcalendar/angular/daygrid"
import timeGridPlugin from "@fullcalendar/angular/timegrid"
import interactionPlugin from "@fullcalendar/angular/interaction"
import {DateClickInfo} from "@fullcalendar/angular"
import deLocale from "@fullcalendar/angular/locales/de"
import {AppointmentEventModel} from '../../models/appointment-events/appointment-event.model';
import listPlugin from "@fullcalendar/angular/list"
import {ConsultingHours} from '../../models/doctor-settings/doctor-settings.model';
import {DeviceDetectionService} from '../../services/device-detection/device-detection.service';
import themePlugin from '@fullcalendar/angular/themes/classic'

import {IconComponent} from '../shared/icon/icon.component';
import {DatePipe} from '@angular/common';

@Component({
    selector: 'app-doctor-appointments',
    standalone: true,
    imports: [
        FullCalendarModule,
        DatePipe
    ],
    templateUrl: './doctor-appointments.component.html',
    styleUrl: './doctor-appointments.component.sass'
})
export class DoctorAppointmentsComponent implements OnInit, OnDestroy {
    @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
    private destroy$: Subject<void> = new Subject<void>()
    doctor: DoctorModel | null = null

    patients: PatientModel[] = []
    todayAppointments: ReservedAppointmentModel[] = []
    thisWeekAppointments: ReservedAppointmentModel[] = []
    nextWeekAppointments: ReservedAppointmentModel[] = []

    // Calendar
    now = new Date();
    firstDay: string = '';
    lastDay: string = '';
    calendarOptions: CalendarOptions = {
        initialView: 'timeGridMonth',
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, themePlugin],
        initialDate: this.now,
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
        },
        allDaySlot: false,
        locale: deLocale,
        views: {
            dayGridMonth: {
                // Use 'short' for weekday names like 'Mo', 'Di' in Month view
                dayHeaderFormat: { weekday: 'short' }
            },
            timeGridWeek: {
                // Your original, more detailed format for Week view
                dayHeaderFormat: {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'numeric',
                    omitCommas: true
                }
            },
            timeGridDay: {
                // Format for Day view
                dayHeaderFormat: {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                }
            }
        },

        slotHeaderFormat: {
            hour: '2-digit',
            minute: '2-digit',
            omitZeroMinute: false,
            meridiem: false,
            hour12: false
        },
        eventTimeFormat: {
            hour: '2-digit',
            minute: '2-digit',
            meridiem: true,
            hour12: false
        },
        displayEventEnd: false,
        displayEventTime: false,
        eventClick: (clickInfo) => {
            clickInfo.jsEvent.preventDefault();
        },
        dateClick: this.handleDateClick.bind(this),
        viewDidMount: this.handleViewDidMount.bind(this),
        datesSet: this.handleDatesSet.bind(this),
    };

    // end region calendar
    isMobile: boolean = false

    constructor(
        private doctorService: DoctorService,
        private dialog: MatDialog,
        private router: Router,
        private patientService: PatientService,
        private infoMessageService: InfoMessageService,
        private deviceDetectionService: DeviceDetectionService
    ) {
        this.isMobile = deviceDetectionService.getIsMobile();
        if (this.isMobile) {
            this.calendarOptions.initialView = 'listWeek'
        } else {
            this.calendarOptions.initialView = 'dayGridMonth'
        }
    }

    ngOnInit(): void {
        this.getMe();
        this.getPatientsForDoctorId(this.doctor?.id || ''); // Falls ID schon da, sonst in getMe
        this.loadOverviewAppointments();
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    handleDateClick(arg: DateClickInfo) {
        const calendarApi = arg.view.calendar;
        calendarApi.changeView('timeGridDay', arg.date);
    }

    handleViewDidMount(arg: { view: any }) {
        // this.updateHeaderToolbar(arg.view.type);
    }

    handleDatesSet(arg: { view: any, startStr: string, endStr: string }) {

        let startDate = new Date(arg.startStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });

        let endDate = new Date(new Date(arg.endStr).getTime() - (24 * 60 * 60 * 1000))
            .toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });

        let titleElement = document.querySelector('.fc-toolbar-title');
        if (titleElement) {
            titleElement.textContent = `${startDate} - ${endDate}`;
        }
    }

    openApplicationCancelModal(appointment: ReservedAppointmentModel) {
        const dialogRef = this.dialog.open(ApplicationCancelDialogComponent, {
            closeOnNavigation: true,
            disableClose: true,
            data: {
                appointment
            }
        })

        dialogRef.afterClosed().subscribe((result: { isCanceled: boolean, reason: string }) => {
            if (result) {
                this.cancelAppointment(this.doctor!.id, appointment.id, result.reason)
            }
        })
    }



    toPatientFile(appointment: ReservedAppointmentModel) {
        this.router.navigate(['patientenakte'], {state:{patient: appointment.patient, appointment: appointment}});
    }

    private getCurrentWeek() {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
        const diffToMonday = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday

        const monday = new Date(today.setDate(diffToMonday));
        const sunday = new Date(today.setDate(diffToMonday + 6));


        this.firstDay = monday.toISOString();
        this.lastDay = sunday.toISOString();

        const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: 'numeric'};
        const mondayStr = monday.toLocaleDateString(undefined, options);
        const sundayStr = sunday.toLocaleDateString(undefined, options);

    }


    private getAppointments(query: QueryModel): void {
        query = { ...query, isConfirmed: true } as QueryModel;
        this.getAppointmentsForDoctor(query)
    }

    private getAppointmentsForDoctor( query: QueryModel): void {
        this.doctorService.getAppointmentsForDoctor(this.doctor!.id, query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<ReservedAppointmentModel>) => {
                    this.doctor!.calendarOptions = {
                        ...this.doctor!.calendarOptions,
                        slotMinTime: this.gatStartTime(this.doctor!.consultingHours!),
                        slotMaxTime: this.getEndTime(this.doctor!.consultingHours!),
                    }
                    this.doctor!.mappedAppointments = this.parseAppointments(response.data);
                },
                error: (error) => {
                }
            })
    }

    private gatStartTime(hours: ConsultingHours[]): string {
        if (hours.length === 0) {
            return '';
        }

        let earliestTime = new Date(hours[0].from);
        hours.forEach(hour => {
            const fromTime = new Date(hour.from);
            if (fromTime < earliestTime) {
                earliestTime = fromTime;
            }
        });

        return this.formatTime(earliestTime);
    }

    private getEndTime(hours: ConsultingHours[]): string {
        if (hours.length === 0) {
            return '';
        }

        let latestTime = new Date(hours[0].to);
        hours.forEach(hour => {
            const toTime = new Date(hour.to);
            if (toTime > latestTime) {
                latestTime = toTime;
            }
        });

        return this.formatTime(latestTime);
    }

    private formatTime(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    }

    private parseAppointments(appointments: ReservedAppointmentModel[]): AppointmentEventModel[] {
        return appointments.map(availableAppointment => {
            const title = availableAppointment.patient.firstName + ' ' + availableAppointment.patient.lastName;
            return {
                id: availableAppointment.from,
                title: title,
                start: availableAppointment.from,
                end: availableAppointment.to,
                backgroundColor: 'rgba(214, 151, 60, 0.1)',
                textColor: '#D6973C',
                allDay: false,
                patient: availableAppointment.patient,
                appointment: availableAppointment
            };
        });
    }

    private loadOverviewAppointments(): void {
        if (!this.doctor?.id) return;

        const now = new Date();

        // Heute
        const todayStart = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        const todayEnd = new Date(now.setHours(23, 59, 59, 999)).toISOString();

        this.doctorService.getAppointmentsForDoctor(this.doctor.id, { from: todayStart, to: todayEnd, isConfirmed: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => this.todayAppointments = res.data);

        // Diese Woche (bis Sonntag)
        const startOfWeek = new Date(); // Jetzt
        const endOfWeek = new Date();
        const daysUntilSunday = 7 - (startOfWeek.getDay() || 7);
        endOfWeek.setDate(startOfWeek.getDate() + daysUntilSunday);
        endOfWeek.setHours(23, 59, 59, 999);

        this.doctorService.getAppointmentsForDoctor(this.doctor.id, { from: startOfWeek.toISOString(), to: endOfWeek.toISOString(), isConfirmed: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => this.thisWeekAppointments = res.data);

        // Nächste Woche
        const startOfNextWeek = new Date(endOfWeek);
        startOfNextWeek.setDate(startOfNextWeek.getDate() + 1);
        startOfNextWeek.setHours(0,0,0,0);

        const endOfNextWeek = new Date(startOfNextWeek);
        endOfNextWeek.setDate(endOfNextWeek.getDate() + 6);
        endOfNextWeek.setHours(23,59,59,999);

        this.doctorService.getAppointmentsForDoctor(this.doctor.id, { from: startOfNextWeek.toISOString(), to: endOfNextWeek.toISOString(), isConfirmed: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => this.nextWeekAppointments = res.data);
    }

    private getPatientsForDoctorId(doctorId: string): void {
        this.patientService.getPatientsForDoctorId(doctorId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientModel>) => {
                    this.patients = response.data
                },
                error: (error) => {
                }
            })
    }

    private getMe(): void {
        this.doctorService.getMyProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorModel>) => {
                    this.doctor = response.data;
                    this.getPatientsForDoctorId(this.doctor.id);
                    this.loadOverviewAppointments();

                    this.doctor!.calendarOptions = {
                        ...this.calendarOptions,
                        slotDuration: '00:' + (this.doctor?.consultingDurationInMinutes?.toString().length == 1 ? '0' + this.doctor?.consultingDurationInMinutes : this.doctor?.consultingDurationInMinutes?.toString()) + ':00',
                        datesSet: (dateInfo) => {
                            const query = {
                                isConfirmed: true,
                                from: dateInfo.startStr,
                                to: dateInfo.endStr
                            }
                            this.getAppointmentsForDoctor(query)
                        },
                        eventClick: (info) => {
                            this.router.navigate(['patientenakte'], {state: {patient: info.event.extendedProps['patient'], appointment: info.event.extendedProps['appointment']}})
                        }
                    }

                    const query = {
                        doctorId: this.doctor!.id,
                        from: this.firstDay,
                        to: this.lastDay
                    }
                    this.getAppointments(query)
                    this.getPatientsForDoctorId(this.doctor.id)
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    private cancelAppointment(doctorId: string, appointmentId: string, reason: string): void {
        this.doctorService.cancelAppointment(doctorId, appointmentId, reason)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.infoMessageService.createMessage('Termin erfolgreich abgesagt', 'success');
                },
                error: (error) => {
                    this.infoMessageService.createMessage('Der Termin kann nicht abgesagt werden, da schon ein Rezept ausgestellt wurde oder der Termin schon stattgefunden hat.', 'error')
                }
            })
    }
}
